// src/components/PrixRemplacements.jsx — Paramètres → « Prix des remplacements »
// (refonte Incidents, spec Dimitri 2026-08-13).
//
// LE PRIX HABITUEL PAR TYPE d'article : posé ICI une fois, affiché en LECTURE
// SEULE sur les cartes d'incident admin (« prix habituel : 45 € ») pour guider
// le rachat sans le contraindre — le coût réel se saisit toujours au cas par
// cas dans la modale.
//
// TABLE `incident_types_prix` (socle 2026-08-13) — RLS admin_all + service_role,
// AUCUNE policy terrain (prouvé au dry-run : 0 ligne lisible côté agent).
// L'écriture est un UPSERT client direct sous RLS admin (Pattern B, comme les
// autres écrans Paramètres). CHECK en base : prix ∈ [0, 10 000].

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { DS } from '../lib/ui'
import { INCIDENT_TYPES } from '../lib/domain-constants'
import { Check, AlertTriangle } from 'lucide-react'

export default function PrixRemplacements() {
  const [prix, setPrix] = useState({})       // { [type]: string (champ contrôlé) }
  const [initial, setInitial] = useState({}) // snapshot chargé (détection des changements)
  const [etat, setEtat] = useState(null)     // null | 'ok' | message d'erreur
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let annule = false
    ;(async () => {
      try {
        const { data, error } = await supabase.from('incident_types_prix').select('type, prix_eur')
        if (error) throw error
        const map = Object.fromEntries((data || []).map((r) => [r.type, String(r.prix_eur)]))
        if (!annule) { setPrix(map); setInitial(map) }
      } catch (e) {
        if (!annule) setEtat(`Lecture impossible : ${e.message || e}`)
      }
    })()
    return () => { annule = true }
  }, [])

  const enregistrer = useCallback(async () => {
    if (busy) return
    setBusy(true); setEtat(null)
    try {
      // On n'écrit QUE les lignes modifiées, et on retire celles vidées.
      const aPoser = []
      const aRetirer = []
      for (const type of INCIDENT_TYPES) {
        const v = (prix[type] ?? '').trim()
        const avant = (initial[type] ?? '').trim()
        if (v === avant) continue
        if (v === '') { aRetirer.push(type); continue }
        const n = Number(v.replace(',', '.'))
        if (!Number.isFinite(n) || n < 0 || n > 10000) {
          throw new Error(`Prix invalide pour « ${type} » (0 à 10 000 €).`)
        }
        aPoser.push({ type, prix_eur: n })
      }
      if (aPoser.length) {
        const { error } = await supabase.from('incident_types_prix')
          .upsert(aPoser, { onConflict: 'type' })
        if (error) throw error
      }
      if (aRetirer.length) {
        const { error } = await supabase.from('incident_types_prix')
          .delete().in('type', aRetirer)
        if (error) throw error
      }
      setInitial({ ...prix })
      setEtat('ok')
      setTimeout(() => setEtat((e) => (e === 'ok' ? null : e)), 2000)
    } catch (e) {
      setEtat(e.message || String(e))
    } finally { setBusy(false) }
  }, [busy, prix, initial])

  const modifie = INCIDENT_TYPES.some((t) => (prix[t] ?? '') !== (initial[t] ?? ''))

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
        {INCIDENT_TYPES.map((type) => (
          <label key={type} style={{ display: 'flex', alignItems: 'center', gap: 8,
            background: DS.card, border: `0.5px solid ${DS.border}`, borderRadius: 9,
            padding: '7px 10px', fontSize: 12, color: DS.marineDark, fontFamily: DS.fontBody }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{type}</span>
            <input
              type="number" min="0" max="10000" step="1" placeholder="—"
              value={prix[type] ?? ''}
              onChange={(e) => setPrix((p) => ({ ...p, [type]: e.target.value }))}
              data-testid={`prix-remplacement-${type}`}
              style={{ width: 72, textAlign: 'right', border: `0.5px solid ${DS.borderHover}`,
                borderRadius: 7, padding: '5px 7px', fontSize: 12, fontFamily: DS.fontBody,
                fontVariantNumeric: 'tabular-nums' }} />
            <span style={{ fontSize: 11, color: DS.textMuted }}>€</span>
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <button type="button" className="fbz-press" onClick={enregistrer}
          disabled={busy || !modifie} data-testid="prix-remplacements-save"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
            background: modifie ? DS.marine : DS.pageBg, color: modifie ? '#fff' : DS.textMuted,
            border: modifie ? 'none' : `0.5px solid ${DS.border}`, borderRadius: DS.radiusControl,
            padding: '8px 14px', fontSize: 12, fontWeight: 700,
            cursor: busy || !modifie ? 'not-allowed' : 'pointer', fontFamily: DS.fontBody }}>
          {busy ? 'Enregistrement…' : 'Enregistrer les prix'}
        </button>
        {etat === 'ok' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: DS.successText, fontWeight: 600 }}>
            <Check size={13} strokeWidth={2.6} /> Enregistré
          </span>
        )}
        {etat && etat !== 'ok' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: DS.dangerText, fontWeight: 600 }}>
            <AlertTriangle size={13} strokeWidth={2.2} /> {etat}
          </span>
        )}
      </div>
      <div style={{ fontSize: 10.5, color: DS.textMuted, marginTop: 8, fontStyle: 'italic' }}>
        Affiché en lecture seule sur les incidents à racheter (« prix habituel »). Champ vide = pas de prix affiché.
      </div>
    </div>
  )
}
